import React, { useEffect, useState } from 'react';
import "../../assets/style/eegisterProvider.css";
import { Button, Typography, Chip } from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ApiCall from '../../utils/Api';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress } from '@mui/material';
import { convertDateFormat, extractTimeFromTimestamp } from "../../utils/services";

const FundSubscriberTransactionDetails = () => {
  const navigate = useNavigate();
  const { id, fundName, levelName } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [subscriberName, setSubscriberName] = useState('');
  const [loading, setLoading] = useState(true);
  const [ethPrice, setEthPrice] = useState(2000);
  const [annualYieldPercent, setAnnualYieldPercent] = useState(0);
  const [userRole, setUserRole] = useState('Admin');

  useEffect(() => {
    // Get user role from localStorage
    const role = localStorage.getItem('role') || 'Admin';
    setUserRole(role);
    
    // Fetch ETH price
    const fetchEthPrice = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };
    fetchEthPrice();
  }, []);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      let dataLoaded = false;
      try {
        setLoading(true);
        
        // Fetch buyer data to get all purchases
        const response = await ApiCall.SECURE_API.get(`/buyer/getAllBuyers`);
        
        if (response.data.success && response.data.data) {
          dataLoaded = true;
          let filteredPurchases = [];
          
          // If fundName and levelName are provided, filter by subscriber + fund + level (Details II)
          if (fundName && levelName) {
            const decodedFundName = decodeURIComponent(fundName || '');
            const decodedLevelName = decodeURIComponent(levelName || '').replace('Level -', '');
            
            filteredPurchases = response.data.data.filter(purchase => {
              const buyerId = purchase?.BuyerConsumer?._id || purchase?.BuyerConsumer || purchase?.BuyerProvider?._id || purchase?.BuyerProvider;
              const purchaseFundName = purchase?.fundUnitName || purchase?.dataUnit?.fundUnitName || purchase?.fundName || '';
              const purchaseLevelName = purchase?.selectedLevel?.levelName || '1';
              
              return String(buyerId) === String(id) && 
                     purchaseFundName === decodedFundName &&
                     String(purchaseLevelName) === String(decodedLevelName);
            });
          } else {
            // If only ID provided, check if it's a data unit ID (from Fund Transactions)
            filteredPurchases = response.data.data.filter(purchase => {
              const dataUnitId = purchase?.dataUnit?._id || purchase?.dataUnit || purchase?.dataUnitId;
              return String(dataUnitId) === String(id);
            });
            
            // If no matches by data unit, treat as subscriber ID
            if (filteredPurchases.length === 0) {
              filteredPurchases = response.data.data.filter(purchase => {
                const buyerId = purchase?.BuyerConsumer?._id || purchase?.BuyerConsumer || purchase?.BuyerProvider?._id || purchase?.BuyerProvider;
                return String(buyerId) === String(id);
              });
            }
          }

          // Get subscriber/fund name based on context
          if (filteredPurchases.length > 0) {
            const firstPurchase = filteredPurchases[0];
            
            // Check if we're filtering by data unit (from Fund Transactions)
            const dataUnitId = firstPurchase?.dataUnit?._id || firstPurchase?.dataUnit || firstPurchase?.dataUnitId;
            if (!fundName && !levelName && String(dataUnitId) === String(id)) {
              // Show fund/data unit name when filtering by data unit
              const fundUnitName = firstPurchase?.fundUnitName || firstPurchase?.dataUnit?.fundUnitName || firstPurchase?.fundName || firstPurchase?.dataUnit?.name || 'Fund';
              // Try to get provider/institution name
              const institutionName = firstPurchase?.institutionName || firstPurchase?.dataUnit?.provider?.institutionName || fundUnitName;
              setSubscriberName(institutionName);
            } else {
              // Show subscriber name
              const buyer = firstPurchase.BuyerConsumer || firstPurchase.BuyerProvider;
              const name = buyer?.firstName && buyer?.lastName 
                ? `${buyer.firstName} ${buyer.lastName}`
                : buyer?.institutionName || 'Fund Subscriber';
              setSubscriberName(name);
            }
            
            // Get annual yield percent
            const yieldPercent = firstPurchase?.selectedLevel?.annualYieldPercent || 
                               firstPurchase?.dataUnit?.pricingLevels?.find(level => 
                                 level.levelName === firstPurchase?.selectedLevel?.levelName
                               )?.annualYieldPercent ||
                               firstPurchase?.dataUnit?.pricingLevels?.[0]?.annualYieldPercent ||
                               0;
            setAnnualYieldPercent(yieldPercent);
          }

          // Process each purchase transaction
          const transactionList = filteredPurchases.map((purchase, index) => {
            const selectedLevel = purchase?.selectedLevel || {};
            const ticketUSD = selectedLevel?.ticketUSD || purchase?.dataUnit?.pricingLevels?.[0]?.ticketUSD || 0;
            const ticketETH = selectedLevel?.ticketETH || purchase?.dataUnit?.pricingLevels?.[0]?.ticketETH || 0;
            
            // Calculate ticket ETH if not available
            let calculatedTicketETH = ticketETH;
            if (!calculatedTicketETH && ticketUSD && ethPrice) {
              calculatedTicketETH = ticketUSD / ethPrice;
            } else if (purchase?.priceWei) {
              // Convert from wei to ETH
              calculatedTicketETH = Number(purchase.priceWei) / 1e18;
            }
            
            const ticketsPurchased = purchase?.ticketsPurchased || 1;
            const purchaseDate = purchase?.purchaseDate || purchase?.createdAt;
            
            // Calculate investment in TDUX (using ticketUSD as TDUX)
            const investmentTDUX = ticketUSD * ticketsPurchased;
            
            // Calculate yield balance: Total annual yield minus monthly payouts
            let yieldBalance = 0;
            let yieldAddedToBalance = 0;
            
            if (annualYieldPercent > 0 && investmentTDUX > 0 && purchaseDate) {
              const purchaseDateObj = new Date(purchaseDate);
              const currentDate = new Date();
              
              // Calculate total annual yield
              const totalAnnualYield = (investmentTDUX * annualYieldPercent) / 100;
              
              // Calculate months elapsed (full months only)
              const monthsElapsed = Math.floor(
                (currentDate.getFullYear() - purchaseDateObj.getFullYear()) * 12 +
                (currentDate.getMonth() - purchaseDateObj.getMonth())
              );
              
              // Monthly payout (1/12th of annual yield)
              const monthlyPayout = totalAnnualYield / 12;
              
              // Yield already added to balance (only full months)
              yieldAddedToBalance = monthlyPayout * Math.max(0, monthsElapsed);
              
              // Remaining yield balance
              yieldBalance = Math.max(0, totalAnnualYield - yieldAddedToBalance);
            }
            
            // Balance = Investment + Yield already added to balance
            const balance = investmentTDUX + yieldAddedToBalance;
            
            // Get transaction hash - ensure we use the purchase transaction hash, not fund creation hash
            // IMPORTANT: Use purchase.transactionHash (purchase transaction), NOT dataUnit.transactionHash (fund creation)
            let transactionHash = purchase?.transactionHash || '';
            
            // Validate that we're not accidentally using the fund creation hash
            const fundCreationHash = purchase?.dataUnit?.transactionHash || '';
            if (transactionHash && fundCreationHash && transactionHash === fundCreationHash) {
              // This might be the fund creation hash, log a warning
              console.warn('Transaction hash matches fund creation hash - this might be incorrect:', transactionHash);
            }
            
            return {
              purchaseDate,
              ticketsPurchased,
              purchasePriceETH: calculatedTicketETH * ticketsPurchased,
              purchasePriceUSD: ticketUSD * ticketsPurchased,
              investmentTDUX,
              yieldBalance,
              balance,
              transactionHash: transactionHash
            };
          });

          // Sort by purchase date (most recent first) - ensure latest transactions appear first
          transactionList.sort((a, b) => {
            const dateA = new Date(a.purchaseDate);
            const dateB = new Date(b.purchaseDate);
            // Most recent first (descending order)
            return dateB - dateA;
          });
          
          setTransactions(transactionList);
        }
      } catch (error) {
        console.error("Error fetching transaction details:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        // Only show error if data wasn't loaded in this attempt AND we don't have existing data
        if (!dataLoaded && transactions.length === 0) {
          toast.error(error.response?.data?.message || "Failed to fetch transaction details");
        } else if (!dataLoaded) {
          // If we have existing data but this fetch failed, just log it
          console.warn("Error fetching fresh data, keeping existing transactions");
        }
        // Don't clear transactions on error - keep existing data
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTransactionDetails();
    }
  }, [id, fundName, levelName, ethPrice]);

  const goBack = () => {
    navigate(-1);
  };

  const formatEth = (eth) => {
    return typeof eth === 'number' ? eth.toFixed(6) : '0.000000';
  };

  const formatUsd = (usd) => {
    return typeof usd === 'number' ? usd.toFixed(2) : '0.00';
  };

  const formatTDUX = (amount) => {
    if (!amount || amount === 0) return '0';
    return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const decodedFundName = decodeURIComponent(fundName || '');
  const decodedLevelName = decodeURIComponent(levelName || '');

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", backgroundColor: "#F3F4F8", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button 
            variant="contained" 
            onClick={goBack} 
            style={{ marginRight: "20px", backgroundColor: "black" }}
          >
            {"< Back"}
          </Button>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Fund Subscribers - Details (II)
          </Typography>
        </div>
        <Chip 
          label={userRole || 'Admin'} 
          sx={{ 
            backgroundColor: userRole === 'Admin' ? '#e3f2fd' : '#fce4ec', 
            color: userRole === 'Admin' ? '#1976d2' : '#c2185b',
            fontWeight: 600,
            fontSize: '12px'
          }} 
        />
      </div>

      {/* Subtitle */}
      <div style={{ padding: '20px 40px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" style={{ fontWeight: 500, color: '#666' }}>
          {subscriberName} - {decodeURIComponent(fundName || '')} - {decodeURIComponent(levelName || '')}
        </Typography>
      </div>
      
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-around", width: "100%", padding: '40px' }}>
        <div style={{ width: "98%" }}>
          <TableContainer component={Paper} style={{ borderRadius: 9 }}>
            <Table sx={{ minWidth: 1200 }} aria-label="transaction details">
              <TableHead style={{ backgroundColor: "Black" }}>
                <TableRow>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Purchase Date and Time</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}># of Tickets</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Purchase Price</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Investment</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Yield Balance</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Balance</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Explorer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" style={{ padding: '40px' }}>
                      <Typography variant="body1" color="textSecondary">
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f8ff' }
                      }}
                    >
                      <TableCell align="center">
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>
                            {convertDateFormat(transaction.purchaseDate)}
                          </span>
                          <span style={{ color: "gray", fontSize: '13px' }}>
                            {extractTimeFromTimestamp(transaction.purchaseDate)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.ticketsPurchased}
                      </TableCell>
                      <TableCell align="center">
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>
                            {formatEth(transaction.purchasePriceETH)} ETH
                          </span>
                          <span style={{ color: "gray", fontSize: '13px' }}>
                            {formatUsd(transaction.purchasePriceUSD)} USD
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px', fontWeight: 500 }}>
                        {formatTDUX(transaction.investmentTDUX)} TDUX
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px', fontWeight: 500 }}>
                        {formatTDUX(transaction.yieldBalance)} TDUX
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px', fontWeight: 500 }}>
                        {formatTDUX(transaction.balance)} TDUX
                      </TableCell>
                      <TableCell align="center">
                        {transaction.transactionHash ? (
                          <a
                            href={`https://sepolia.etherscan.io/tx/${transaction.transactionHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#1976d2', 
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            Link on Explorer
                          </a>
                        ) : (
                          <span style={{ 
                            color: '#999', 
                            fontSize: '14px',
                            cursor: 'not-allowed'
                          }}>
                            No Link
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default FundSubscriberTransactionDetails;

